
import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Typography,
  notification,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  Upload
} from "antd";
import axios from "axios";
import { UserOutlined, PhoneOutlined, CalendarOutlined, CheckCircleOutlined, UploadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useHistory, useParams } from "react-router-dom";
import { baseUrl } from "../../config";

const { Title, Text } = Typography;
const { Item } = Form;
const { Option } = Select;
const { TextArea } = Input;

function VisheshSahyogiForm({ userId, onRefreshData = () => {} }) {
  const history = useHistory();
  const [form] = Form.useForm();
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [txnDate, setTxnDate] = useState(null);
  const [meetingDate, setMeetingDate] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (userId) {
      setIsUpdateMode(true);
      fetchVisheshSahyogiDetails();
    }
  }, [userId]);

  const fetchVisheshSahyogiDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${baseUrl}/vishesh_sahyogi/read/${userId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      if (response.status === 200) {
        const data = response.data.result;
        form.setFieldsValue(data);
        
        // Handle DOB
        if (data.dob && data.dob !== '0000-00-00' && moment(data.dob).isValid()) {
          setTxnDate(data.dob);
        } else {
          setTxnDate(null);
        }
        
        // Handle Meeting Date
        if (data.meeting_date && data.meeting_date !== '0000-00-00' && moment(data.meeting_date).isValid()) {
          setMeetingDate(data.meeting_date);
        } else {
          setMeetingDate(null);
        }
         
        // Handle Profile Photo
        if (data.profile_photo) {
          setImageUrl(data.profile_photo);
          setFileList([
            {
              uid: '-1',
              name: 'Profile Photo',
              status: 'done',
              url: data.profile_photo,
            },
          ]);
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to fetch Vishesh Sahyogi details',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
    }
  };


  // Fixed VisheshSahyogiForm.js - onFinish method
const onFinish = async (values) => {
  try {
    setLoading(true);
    const formData = new FormData();

    // ✅ Fix: Proper date handling - send null instead of empty strings
    if (txnDate && txnDate !== '0000-00-00' && moment(txnDate).isValid()) {
      const formattedDate = moment(txnDate).format('YYYY-MM-DD');
      formData.append('dob', formattedDate);
    }
    // Don't append dob field if it's empty/invalid

    if (meetingDate && meetingDate !== '0000-00-00' && moment(meetingDate).isValid()) {
      const formattedMeetingDate = moment(meetingDate).format('YYYY-MM-DD');
      formData.append('meeting_date', formattedMeetingDate);
    }
    // Don't append meeting_date field if it's empty/invalid

    // Append other form fields (skip null/undefined values)
    Object.keys(values).forEach(key => {
      if (key !== 'image' && values[key] !== undefined && values[key] !== null && values[key] !== '') {
        formData.append(key, values[key]);
      }
    });

    // Handle image upload
    if (fileList.length > 0 && fileList[0].originFileObj) {
      formData.append('image', fileList[0].originFileObj);
    }

    // Make API request
    let response;
    if (isUpdateMode) {
      response = await axios.patch(`${baseUrl}/vishesh_sahyogi/update/${userId}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data'
        },
      });
    } else {
      response = await axios.post(`${baseUrl}/vishesh_sahyogi/create`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'multipart/form-data'
        },
      });
    }

    if (response.status === 200) {
      notification.success({
        message: 'Success',
        description: isUpdateMode ? 'Vishesh Sahyogi updated successfully!' : 'Vishesh Sahyogi added successfully!',
        placement: 'topRight'
      });
      
      onRefreshData();
      
      if (!isUpdateMode) {
        form.resetFields();
        setTxnDate(null);
        setMeetingDate(null);
        setFileList([]);
        setImageUrl(null);
      }
    }
  } catch (error) {
    console.error('Form submission error:', error);
    notification.error({
      message: 'Error',
      description: error.response?.data?.message || 'Operation failed',
      placement: 'topRight'
    });
  } finally {
    setLoading(false);
  }
};

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setTxnDate(formattedDate);
    } else {
      setTxnDate(null);
    }
  };

  const handleMeetingDateChange = (date) => {
    if (date) {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      setMeetingDate(formattedDate);
    } else {
      setMeetingDate(null);
    }
  };

  const handleImageChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
    if (!isJpgOrPng) {
      notification.error({
        message: 'Error',
        description: 'You can only upload JPG/PNG files!',
        placement: 'topRight'
      });
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      notification.error({
        message: 'Error',
        description: 'Image must be smaller than 2MB!',
        placement: 'topRight'
      });
      return false;
    }
    return false; // Prevent auto upload
  };

  return (
      <Form 
        form={form} 
        onFinish={onFinish} 
        layout="vertical"
        requiredMark={false}
        style={{ maxWidth: '100%' }}
      >
        <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>Personal Details</div>
        <Row gutter={[20, 16]}>
          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Upload Profile Photo</Text>}
              name="profilePhoto"
            >
              <Upload
                name="image"
                listType="picture-card"
                accept="image/*"
                beforeUpload={beforeUpload}
                onChange={handleImageChange}
                fileList={fileList}
                maxCount={1}
                style={{ width: '100%' }}
              >
                {fileList.length === 0 && (
                  <div>
                    <UploadOutlined />
                    <div style={{ marginTop: 8 }}>Upload Photo</div>
                  </div>
                )}
              </Upload>
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Phone Number</Text>}
              name="phone_no"
              rules={[
                { required: true, message: 'Please enter phone number' },
                { pattern: /^\d{10,12}$/, message: 'Phone must be 10-12 digits!' }
              ]}
            >
              <Input
                prefix={<PhoneOutlined style={{ color: '#7a0000' }} />}
                placeholder="Enter phone number"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Order Number</Text>}
              name="order_key"
            >
              <InputNumber
                placeholder="Enter order number"
                style={{ width: '100%', borderRadius: '8px', height: '40px' }}
                min={1}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Full Name</Text>}
              name="name"
              rules={[{ required: true, message: 'Please enter name' }]}
            >
              <Input
                prefix={<UserOutlined style={{ color: '#7a0000' }} />}
                placeholder="Enter full name"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Father's Name</Text>}
              name="father_name"
            >
              <Input
                placeholder="Enter father's name"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Father In Law</Text>}
              name="father_in_low"
            >
              <Input
                placeholder="Enter father in law's name"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Gotra</Text>}
              name="gotra"
            >
              <Input
                placeholder="Enter gotra"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>Location Details</div>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Village</Text>}
              name="village"
            >
              <Input
                placeholder="Enter village"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Tehsil</Text>}
              name="tehsil"
            >
              <Input
                placeholder="Enter tehsil"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>District</Text>}
              name="district"
            >
              <Input
                placeholder="Enter district"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Occupation</Text>}
              name="occupation"
            >
              <Input
                placeholder="Enter occupation"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Occupation District</Text>}
              name="occ_district"
            >
              <Input
                placeholder="Enter occupation district"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Contribution Remark</Text>}
              name="inspiration_remark"
            >
              <Input
                placeholder="Enter contribution remark"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Amount Pledge</Text>}
              name="amount_pledge"
            >
              <InputNumber
                placeholder="Enter amount"
                style={{ width: '100%', borderRadius: '8px', height: '40px' }}
                formatter={value => `₹ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value.replace(/₹\s?|(,*)/g, '')}
                min={0}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Date of Birth</Text>}
            >
              <DatePicker
                onChange={handleDateChange}
                value={txnDate && txnDate !== "0000-00-00" && moment(txnDate).isValid() ? moment(txnDate) : null}
                format="DD-MM-YYYY"
                placeholder="Select date of birth"
                style={{ width: '100%', borderRadius: '8px', height: '40px' }}
                suffixIcon={<CalendarOutlined style={{ color: '#7a0000' }} />}
                allowClear
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Alternative Number</Text>}
              name="alternative_no"
            >
              <Input
                placeholder="Enter alternative number"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0', marginTop: 8 }}>Meeting & Contribution</div>
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Committee</Text>}
              name="committee"
            >
              <Input
                placeholder="Enter committee"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Meeting Attendance</Text>}
              name="meeting_attendance"
            >
              <Input
                placeholder="Enter meeting attendance"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Meeting Remark</Text>}
              name="meeting_remark"
            >
              <Input
                placeholder="Enter meeting remark"
                style={{ borderRadius: '8px', height: '40px' }}
              />
            </Item>
          </Col>

          <Col xs={24} sm={12} lg={8}>
            <Item
              label={<Text strong>Meeting Date</Text>}
            >
              <DatePicker
                onChange={handleMeetingDateChange}
                value={meetingDate && meetingDate !== "0000-00-00" && moment(meetingDate).isValid() ? moment(meetingDate) : null}
                format="DD-MM-YYYY"
                placeholder="Select meeting date"
                style={{ width: '100%', borderRadius: '8px', height: '40px' }}
                suffixIcon={<CalendarOutlined style={{ color: '#7a0000' }} />}
                allowClear
              />
            </Item>
          </Col>
        </Row>

        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
          <Button style={{ borderRadius: 8 }} onClick={() => onRefreshData()}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            style={{ background: '#7a0000', borderColor: '#7a0000', borderRadius: 8, minWidth: 120 }}
          >
            {isUpdateMode ? 'Update Member' : 'Save Member'}
          </Button>
        </div>
      </Form>
  );
}

export default VisheshSahyogiForm;
